<template>
 <!--
   控除コンポーネント(子)

   **id ではなく class。** 元は id="holidaywork" だった（休日出勤の
   コンポーネントからの写し間違い）。この部品は1か月ぶん繰り返し描かれるので、
   同じ id が31個並び、さらに休日出勤側の31個とも重なっていた。
 -->
 <div class="roster-cell roster-cell--deduction">
  <p class="roster-cell__value">
    <span>{{ deductResult[index] }}</span>
  </p>
 </div>
</template>

<script>
import moment from 'moment';
import ConstList from './../ConstList';

export default {
  name: 'Deduction',
  props: {
    monthDay: Array,
    startBasic: Array,
    endBasic: Array,
    breakBasic: Array,
    start: Array,
    end: Array,
    rest: Array,
    attendance1: Array,
    attendance2: Array,
    // 会社の規定勤務時間（自社設定の work_basic）。日ごとに配られる。
    // 終日欠勤の控除に使う。OverTime.vue も同じものを受け取っている。
    workBasic: Array,
    index: Number
  },
  computed: {
    /*
     * 控除は「値を返す」だけにする。
     *
     * 以前は null を返す computed が data の配列を splice で書き換え、
     * その配列を deep watch が拾って親へ渡していた。computed は読まれた
     * ときにしか動かないので、**いつ親へ届くかが描画の順番まかせ**になる。
     *
     * 2026-08-07、勤怠を常勤からシフトへ直した保存で、日次には直った値
     * （控除なし）が入り、月次合計だけ古い 01:00 が入った。月次集計の
     * 欠勤時間は画面が送ってきた合計をそのまま保存する（サーバは差し替え
     * ない）ので、**勤務表 0:00 / 月次集計 1:00 が出し直しても直らない**
     * 状態になった。
     *
     * **ただし、この直しがあの障害の原因を直したとは言えない。**
     * 試験で再現したのは「部品だけを動かすと親が古い値を持ち続ける」ことで、
     * 本番で起きた形そのものではない。当時の本番JSは現在のソースと同一で、
     * 画面の連鎖を組んでも再現しなかった。原因は未解明のまま。
     * ここは同型の壊れ方を防ぐ防御強化であって、調査を終える根拠ではない。
     *
     * 時間外（OverTime.vue）は先に同じ形へ直してある。そちらに合わせる。
     */
    deductResult () {
      return (this.start || []).map((_, i) => this.deductionAt(i));
    }
  },
  watch: {
    /*
     * 値が変わったら必ず親へ渡す。immediate が無いと、誰かがこの computed を
     * 読むまで親は古い値を持ち続ける。
     *
     * **emit は $nextTick へ回すこと。** 親（Roster.vue）は受け取った値を
     * $set で配列へ書き戻す。描画の最中に同期で emit すると、子を patch して
     * いる途中で親の更新が走り、$scopedSlots を読めずに落ちる。
     * 勤務表は1か月ぶんの子をまとめて描くので、必ず踏む。
     */
    deductResult: {
      immediate: true,
      deep: true,
      handler (values) {
        const value = values[this.index];
        this.$nextTick(() => {
          this.$emit('updateDeduct', {
            index: this.index,
            value
          });
        });
      }
    }
  },
  methods: {
    /*
     * 1日ぶんの控除を出す。
     *
     * **計算そのものは変えていない。** 1年以上の運用実績がある値なので、
     * 直したのは「いつ親へ渡すか」だけ。
     */
    /*
     * その日の規定勤務時間。
     *
     * 未設定・空・"00:00" は 08:00 として扱う。バックエンドも
     * AttendanceBufferEntity で work_basic が null なら 08:00 を返すので、
     * 既定値をここだけ変えると画面とサーバがずれる。
     * Roster.vue は値が無い日に "00:00" を入れるため、そこも拾う。
     */
    workBasicAt (i) {
      const value = (this.workBasic || [])[i];
      if (!value || value === '00:00' || value === '00:00:00') {
        return '08:00';
      }
      return String(value).slice(0, 5);
    },

    deductionAt (i) {
      if (
        this.attendance1[i] === ConstList.HOLIDAY ||
        this.attendance1[i] === ConstList.PAID ||
        this.attendance1[i] === ConstList.SUBSTITUTE ||
        this.attendance1[i] === ConstList.SPECIAL_HOLIDAY
      ) {
        return "";
      }

      /*
       * 終日欠勤は、会社の規定勤務時間ぶんを控除する。
       *
       * **ここは長く '08:00' と直接書いてあった。**「自社の標準時間を控除
       * （弊社８H）」という TODO が残ったまま、自社の8時間だけが入っていた。
       * 所定が8時間でない会社・月では、控除が実態と合わない。
       * 自社設定の「規定勤務時間」（work_basic）は画面から編集でき、
       * 未設定なら 08:00 になる。同じ値を時間外（OverTime.vue）は既に
       * 使っている。控除だけが取り残されていた。
       */
      if (this.attendance1[i] === ConstList.DEDUCTION) {
        return this.workBasicAt(i);
      }

      /*
       * 半日の欠勤は、規定勤務時間の半分を控除する。
       *
       * **2026-09-02 に欠勤の休暇申請を足したとき、午前欠勤(11)と
       * 午後欠勤(16)を勤務表で選べるようにした。だが控除はここを
       * 通らず 0 のままだった。**
       *
       *   午前欠勤＋午後出勤 → 下の「午後出勤は遅刻扱いにしない」に
       *                        当たり、13:00 出社の遅れが消える
       *   午前出勤＋午後欠勤 → 下の「午前出勤は早退扱いにしない」に
       *                        当たり、12:00 退社の早上がりが消える
       *
       * どちらも**休んだ半日が無給にならない。**終日欠勤だけが
       * 規定勤務時間ぶんを控除していて、半日には通り道が無かった。
       *
       * 端数は少ないほうへ寄せる。控除は減額なので、割り切れない
       * ときに従業員が損をしないほうへ倒す。
       */
      /*
       * **足し算にする。**前は「午前欠勤 または 午後欠勤」で、
       * 両方選んだ日でも半日ぶんしか引かれなかった。
       * 1日まるごと休んでいるのに控除が半分。
       *
       * 勤務表では同じ種類の午前＋午後を選べないようにしたが、
       * **すでに保存されている行は残る。**そちらも正しく引く。
       */
      const 半日 = Math.floor(
        moment.duration(this.workBasicAt(i), 'HH:mm').asMinutes() / 2);
      let halfDayAbsence = 0;
      if (this.attendance1[i] === ConstList.MORNING_DEDUCTION) {
        halfDayAbsence += 半日;
      }
      if (this.attendance2[i] === ConstList.AFTERNOON_DEDUCTION) {
        halfDayAbsence += 半日;
      }

      let sTime = moment.duration(this.start[i], 'HH:mm');
      let eTime = moment.duration(this.end[i], 'HH:mm');
      let sBasic;
      let eBasic;

      // 午前出勤・午後有給・午後代休・午後特休・シフトは早退扱いにしない
      // 午後欠勤は上で半日ぶんを引いているので、早退として二重に引かない
      if (this.attendance1[i] === ConstList.MORNING_WORK
        || this.attendance2[i] === ConstList.AFTERNOON_PAID
        || this.attendance2[i] === ConstList.AFTERNOON_SUBSTITUTE
        || this.attendance2[i] === ConstList.AFTERNOON_SPECIAL_HOLIDAY
        || this.attendance2[i] === ConstList.AFTERNOON_DEDUCTION
        || this.attendance2[i] === ConstList.SHIFT
      ) {
        eBasic = moment.duration(this.end[i], 'HH:mm');
      } else {
        eBasic = moment.duration(this.endBasic[i], 'HH:mm');
      }

      // 午後出勤・午前有給・午前代休・午前特休・シフトは遅刻扱いにしない
      // 午前欠勤は上で半日ぶんを引いているので、遅刻として二重に引かない
      if (this.attendance2[i] === ConstList.AFTERNOON_WORK
        || this.attendance1[i] === ConstList.MORNING_PAID
        || this.attendance1[i] === ConstList.MORNING_SUBSTITUTE
        || this.attendance1[i] === ConstList.MORNING_SPECIAL_HOLIDAY
        || this.attendance1[i] === ConstList.MORNING_DEDUCTION
        || this.attendance2[i] === ConstList.SHIFT
      ) {
        sBasic = moment.duration(this.start[i], 'HH:mm');
      } else {
        sBasic = moment.duration(this.startBasic[i], 'HH:mm');
      }

      // 時間差分設定（subtract は受け手を書き換えるので sTime / eBasic が差分になる）
      const sDiff = sTime.subtract(sBasic);
      const eDiff = eBasic.subtract(eTime);

      // 00:00は空白
      // **ただし半日欠勤ぶんは残す。**時刻が未入力でも休んだ事実は消えない。
      if ((eTime.hours() === 0 && eTime.minutes() === 0) ||
          (sBasic.hours() === 0 && sBasic.minutes() === 0)) {
        return this.formatMinutes(halfDayAbsence);
      }

      /*
       * 遅刻と早退を足す。**マイナスは 0 にしてから足す。**
       *
       * 前は「片方だけプラスならそちらを返す／両方なら足す」と
       * 三分岐で書いてあった。**結果は同じ**——早く来たぶんで
       * 早退を相殺しない、という同じ約束を短く書いただけ。
       * ここへ半日欠勤ぶんを足す必要があったので一本にまとめた。
       */
      const late = sDiff.asMinutes() > 0 ? sDiff.asMinutes() : 0;
      const early = eDiff.asMinutes() > 0 ? eDiff.asMinutes() : 0;
      return this.formatMinutes(late + early + halfDayAbsence);
    },
    /**
     * 分を HH:mm にする。0 以下は空白。
     *
     * <p>formatTime は moment の duration を受け取り、24時間で
     * 折り返す。控除は分で足し合わせたいのでこちらを使う。</p>
     */
    formatMinutes (total) {
      if (!total || total <= 0) {
        return "";
      }
      const h = Math.floor(total / 60);
      const m = Math.round(total % 60);
      return (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m);
    },
    formatTime(duration) {
      const hours = duration.hours() < 10 ? '0' + duration.hours() : duration.hours();
      const minutes = duration.minutes() < 10 ? '0' + duration.minutes() : duration.minutes();
      return hours + ':' + minutes;
    }
  }
};
</script>


<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped>
h1,
h2 {
  font-weight: normal;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}
</style>
